
import React, { useEffect, useState } from "react";
import defaultFront from "../../assets/default-front.jpg";
import defaultBack from "../../assets/default-back.jpg";
import { useNavigate } from "react-router-dom";
import API_URL from "../../services/api";
import "./BookStore.css";

const BookStore = () => {
  const navigate = useNavigate();

  const [books, setBooks] = useState([]);
  const [search, setSearch] = useState("");
  const [genre, setGenre] = useState("All");
  const [language, setLanguage] = useState("All");
  const [selectedBook, setSelectedBook] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // -----------------------------
  // FETCH BOOKS
  // -----------------------------
  const fetchBooks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/books`);
      const data = await res.json();

      if (Array.isArray(data)) {
        setBooks(data);
      } else {
        setBooks([]);
      }
    } catch (err) {
      console.log("Books Error:", err);
      setBooks([]);
    }
  };

  // -----------------------------
  // FETCH CART
  // -----------------------------
  const fetchCart = async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      setCart([]);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/cart/${user.user_id}`);
      const data = await res.json();

      if (Array.isArray(data)) {
        setCart(data);
      } else {
        setCart([]);
      }
    } catch (err) {
      console.log("Cart Error:", err);
      setCart([]);
    }
  };

  useEffect(() => {
    fetchBooks();
    fetchCart();
  }, []);

  // -----------------------------
  // DYNAMIC LANGUAGES
  // -----------------------------
  const languages = [
    ...new Set(
      books
        .map((book) => book.language)
        .filter((lang) => lang && lang.trim() !== "")
    ),
  ].sort();

  // -----------------------------
  // ADD TO CART
  // -----------------------------
  const addToCart = async (book) => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      alert("Please login first.");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/cart/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.user_id,
          book_id: book.book_id,
        }),
      });

      const data = await res.json();

      alert(data.message);
      fetchCart();
    } catch (err) {
      console.log("Add Cart Error:", err);
    }
  };

  // -----------------------------
  // FILTER BOOKS
  // -----------------------------
  const filteredBooks = books
    .filter((book) => Number(book.is_for_sale) === 1)
    .filter((book) => {
      const searchText = search.toLowerCase();

      const matchSearch =
        book.title?.toLowerCase().includes(searchText) ||
        book.author?.toLowerCase().includes(searchText) ||
        book.genre?.toLowerCase().includes(searchText);

      const matchGenre =
        genre === "All" || book.genre === genre;

      const matchLanguage =
        language === "All" || book.language === language;

      return matchSearch && matchGenre && matchLanguage;
    });

  // -----------------------------
  // CART TOTAL
  // -----------------------------
  const cartTotal = cart.reduce(
    (sum, item) =>
      sum + Number(item.price || 0) * Number(item.quantity || 0),
    0
  );

  // -----------------------------
  // BUY NOW
  // -----------------------------
  const buyNow = (book) => {
    const buyNowItem = [
      {
        id: book.id,
        book_id: book.book_id,
        title: book.title,
        author: book.author,
        price: Number(book.price),
        quantity: 1,
        front_cover: book.front_cover,
      },
    ];

    navigate("/user/checkout", {
      state: {
        cart: buyNowItem,
        total: Number(book.price),
        buyNow: true,
      },
    });
  };

  // -----------------------------
  // DELETE CART ITEM
  // -----------------------------
  const removeCartItem = async (id) => {
    try {
      await fetch(`${API_URL}/api/cart/remove/${id}`, {
        method: "DELETE",
      });

      fetchCart();
    } catch (err) {
      console.log("Remove Cart Error:", err);
    }
  };

  // -----------------------------
  // CLEAR CART
  // -----------------------------
  const clearCart = async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) return;

    try {
      await fetch(`${API_URL}/api/cart/clear/${user.user_id}`, {
        method: "DELETE",
      });

      fetchCart();
    } catch (err) {
      console.log("Clear Cart Error:", err);
    }
  };

  return (
    <div className="bookstore-container">

      {/* ================= CART ICON ================= */}
      <div
        className="bookstore-cart-icon"
        onClick={() => {
          setSelectedBook(null);
          setShowCart(true);
        }}
      >
        🛒
        <span className="bookstore-cart-count">
          {cart.length}
        </span>
      </div>

      {/* ================= SUB HEADING ================= */}
      <p className="bookstore-subheading">
        Browse and purchase books available in our bookstore.
      </p>

      {/* ================= SEARCH & FILTERS ================= */}
      <div className="bookstore-search-container">

        <input
          type="text"
          placeholder="🔍 Search by title, author or genre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bookstore-search-input"
        />

        {/* GENRE */}
        <select
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
          className="bookstore-filter"
        >
          <option value="All">All Genres</option>

          <option value="Fiction">Fiction</option>
          <option value="Non-fiction">Non-fiction</option>
          <option value="Fantasy">Fantasy</option>
          <option value="Science Fiction">Science Fiction</option>
          <option value="Mystery">Mystery</option>
          <option value="Horror">Horror</option>
          <option value="Romance">Romance</option>
          <option value="Adventure">Adventure</option>
          <option value="Biography">Biography</option>
          <option value="Autobiography">Autobiography</option>
          <option value="Self-help">Self-help</option>

          <option value="Physics">Physics</option>
          <option value="Chemistry">Chemistry</option>
          <option value="Biology">Biology</option>
          <option value="Computer Science">
            Computer Science
          </option>

          <option value="Algebra">Algebra</option>
          <option value="Geometry">Geometry</option>
          <option value="Calculus">Calculus</option>
          <option value="Statistics">Statistics</option>

          <option value="History">History</option>
          <option value="Geography">Geography</option>
          <option value="Civics">Civics</option>
          <option value="Economics">Economics</option>

          <option value="Programming">Programming</option>
          <option value="Web Development">
            Web Development
          </option>
          <option value="Networking">Networking</option>
          <option value="Databases">Databases</option>

          <option value="English">English</option>
          <option value="Urdu">Urdu</option>
          <option value="Grammar">Grammar</option>
          <option value="Literature">Literature</option>
        </select>

        {/* DYNAMIC LANGUAGE FILTER */}
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="bookstore-filter"
        >
          <option value="All">All Languages</option>

          {languages.map((lang) => (
            <option key={lang} value={lang}>
              {lang}
            </option>
          ))}
        </select>
      </div>

      {/* ================= BOOK COUNT ================= */}
      <h3 className="bookstore-book-count">
        📚 Showing {filteredBooks.length} Books
      </h3>

      {/* ================= BOOK GRID ================= */}
      <div className="bookstore-grid">

        {filteredBooks.length === 0 ? (
          <div className="bookstore-no-books">
            <h3>No books found</h3>
            <p>
              Try changing your search or filters.
            </p>
          </div>
        ) : (
          filteredBooks.map((book) => (
            <div
              key={book.book_id}
              className="bookstore-card"
              onClick={() => setSelectedBook(book)}
            >

              <img
                src={
                  book.front_cover
                    ? `${API_URL}/uploads/books/${book.front_cover}`
                    : defaultFront
                }
                alt={book.title}
                className="bookstore-image"
              />

              <div className="bookstore-grid-data">

                <h3 className="bookstore-title">
                  {book.title}
                </h3>

                <p className="bookstore-author">
                  <b>Author:</b> {book.author}
                </p>

                <p className="bookstore-stock">
                  In Stock
                </p>

                <p className="bookstore-price">
                  <b>Price:</b> Rs. {book.price}
                </p>

                <button
                  className="bookstore-cart-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    addToCart(book);
                  }}
                >
                  Add to Cart
                </button>

                <button
                  className="bookstore-buy-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    buyNow(book);
                  }}
                >
                  Buy Now
                </button>

              </div>
            </div>
          ))
        )}
      </div>

      {/* ================= SHOPPING CART ================= */}
      {showCart && (
        <>
          <div
            className="bookstore-cart-overlay"
            onClick={() => setShowCart(false)}
          />

          <div className="bookstore-cart-drawer">

            <div className="bookstore-cart-header">
              <h2>🛒 Shopping Cart</h2>

              <button
                className="bookstore-close-icon"
                onClick={() => setShowCart(false)}
              >
                ✕
              </button>
            </div>

            <div className="bookstore-cart-body">

              {cart.length === 0 ? (
                <div className="bookstore-empty-cart">
                  <h3>Your cart is empty</h3>
                  <p>
                    Add books to continue shopping.
                  </p>
                </div>
              ) : (
                cart.map((item) => (
                  <div
                    key={item.id}
                    className="bookstore-cart-item"
                  >

                    <img
                      src={
                        item.front_cover
                          ? `${API_URL}/uploads/books/${item.front_cover}`
                          : defaultFront
                      }
                      alt={item.title}
                      className="bookstore-cart-image"
                    />

                    <div className="bookstore-cart-item-info">

                      <h4>{item.title}</h4>

                      <p>{item.author}</p>

                      <p>
                        Qty: <b>{item.quantity}</b>
                      </p>

                      <p>
                        Rs. {item.price}
                      </p>

                      <p className="bookstore-cart-item-total">
                        Total: Rs.{" "}
                        {Number(item.price) *
                          Number(item.quantity)}
                      </p>

                    </div>

                    <button
                      className="bookstore-delete-btn"
                      onClick={() =>
                        removeCartItem(item.id)
                      }
                    >
                      🗑
                    </button>

                  </div>
                ))
              )}
            </div>

            {/* CART FOOTER */}
            {cart.length > 0 && (
              <div className="bookstore-cart-footer">

                <h3>
                  Total: Rs. {cartTotal}
                </h3>

                <button
                  className="bookstore-checkout-btn"
                  onClick={() => {
                    navigate("/user/checkout", {
                      state: {
                        cart,
                        total: cartTotal,
                      },
                    });
                  }}
                >
                  Proceed to Checkout
                </button>

                <button
                  className="bookstore-clear-btn"
                  onClick={clearCart}
                >
                  Clear Cart
                </button>

              </div>
            )}
          </div>
        </>
      )}

      {/* ================= BOOK DETAILS MODAL ================= */}
      {selectedBook && (
        <div className="bookstore-modal-overlay">

          <div className="bookstore-modal">

            <button
              className="bookstore-modal-close"
              onClick={() => setSelectedBook(null)}
            >
              ✕
            </button>

            <h2 className="bookstore-modal-title">
              {selectedBook.title}
            </h2>

            <div className="bookstore-cover-container">

              <div>
                <h4>Front Cover</h4>

                <img
                  src={
                    selectedBook.front_cover
                      ? `${API_URL}/uploads/books/${selectedBook.front_cover}`
                      : defaultFront
                  }
                  alt={selectedBook.title}
                  className="bookstore-cover"
                />
              </div>

              <div>
                <h4>Back Cover</h4>

                <img
                  src={
                    selectedBook.back_cover
                      ? `${API_URL}/uploads/books/${selectedBook.back_cover}`
                      : defaultBack
                  }
                  alt="Back Cover"
                  className="bookstore-cover"
                />
              </div>

            </div>

            <div className="bookstore-details">

              <p>
                <strong>Author:</strong>{" "}
                {selectedBook.author}
              </p>

              <p>
                <strong>Genre:</strong>{" "}
                {selectedBook.genre}
              </p>

              <p>
                <strong>Language:</strong>{" "}
                {selectedBook.language}
              </p>

              <p>
                <strong>Publisher:</strong>{" "}
                {selectedBook.publisher}
              </p>

              <p>
                <strong>Publication Year:</strong>{" "}
                {selectedBook.publication_year}
              </p>

              <p>
                <strong>Description:</strong>
                <br />
                {selectedBook.description ||
                  "No description available."}
              </p>

              <p className="bookstore-modal-price">
                <strong>Price:</strong>{" "}
                Rs. {selectedBook.price}
              </p>

            </div>

            <button
              className="bookstore-modal-close-btn"
              onClick={() => setSelectedBook(null)}
            >
              Close
            </button>

          </div>
        </div>
      )}

    </div>
  );
};

export default BookStore;
